import type { ReactNode } from 'react';
import { Box } from '../box/box';
import { Typography } from '../typography/typography';
import {
    tableShell,
    table,
    headerCell,
    bodyCell,
    bodyRow,
    alignEnd,
} from './data-table.css';

export interface DataTableColumn<Row> {
    /** Stable column id, used as the React key for cells. */
    key: string;
    /** Column heading. */
    header: string;
    /** Cell renderer for a row. */
    render: (row: Row) => ReactNode;
    /** Horizontal alignment of the column (default `start`). */
    align?: 'start' | 'end';
}

export interface DataTableProps<Row> {
    columns: ReadonlyArray<DataTableColumn<Row>>;
    rows: ReadonlyArray<Row>;
    /** Stable key for each row. */
    keyExtractor: (row: Row) => string;
    /** Shown in place of the table body when there are no rows. */
    emptyState?: ReactNode;
    testID?: string;
}

/**
 * Semantic, accessible data table. Renders a real `<table>` with a header row and
 * one row per record — no fixed row/column sizes, so cells grow with enlarged
 * text, and the shell scrolls horizontally rather than clipping on narrow
 * screens. Presentational only: sorting/filtering happen upstream.
 */
export function DataTable<Row>({
    columns,
    rows,
    keyExtractor,
    emptyState,
    testID,
}: DataTableProps<Row>) {
    if (rows.length === 0) {
        return (
            <Box
                background="bg-card"
                borderColor="border-card"
                borderWidth="0165"
                borderRadius="300"
                padding="500"
                testID={testID}
            >
                {typeof emptyState === 'string' || emptyState == null ? (
                    <Typography tone="text-secondary">
                        {emptyState ?? 'Nothing to show.'}
                    </Typography>
                ) : (
                    emptyState
                )}
            </Box>
        );
    }

    return (
        <div className={tableShell} data-testid={testID}>
            <table className={table}>
                <thead>
                    <tr>
                        {columns.map((column) => (
                            <th
                                key={column.key}
                                scope="col"
                                className={
                                    column.align === 'end'
                                        ? `${headerCell} ${alignEnd}`
                                        : headerCell
                                }
                            >
                                {column.header}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => (
                        <tr key={keyExtractor(row)} className={bodyRow}>
                            {columns.map((column) => (
                                <td
                                    key={column.key}
                                    className={
                                        column.align === 'end'
                                            ? `${bodyCell} ${alignEnd}`
                                            : bodyCell
                                    }
                                >
                                    {column.render(row)}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
